import type {
  BuilderLegalEntityDealEvent,
  BuilderLegalStructure,
  BuilderLegalStructureEntity,
  BuilderLegalStructureGroup,
} from "@/lib/api/databook";

export interface LegalStructureValidationError {
  path: string;
  message: string;
}

export interface LegalStructureAnalysisInterval {
  financialYearEndMonth: number;
  startFiscalYear: number;
  endFiscalYear: number;
}

export interface LegalEntityDealEventPresentation {
  label:
    | "Acquisition"
    | "Mid-period acquisition"
    | "Disposal"
    | "Mid-period disposal";
  detail: string;
}

const DEAL_EVENT_MONTH_PATTERN = /^\d{4}-(0[1-9]|1[0-2])$/;
const CURRENCY_CODE_PATTERN = /^[A-Z]{3}$/;
const MONTH_LABELS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

function eventFiscalYear(
  effectiveMonth: string,
  financialYearEndMonth: number,
): number | null {
  if (!DEAL_EVENT_MONTH_PATTERN.test(effectiveMonth)) return null;
  const [year, month] = effectiveMonth.split("-").map(Number);
  return month <= financialYearEndMonth ? year : year + 1;
}

export function isMidPeriodAcquisition(
  event: BuilderLegalEntityDealEvent,
  interval?: LegalStructureAnalysisInterval,
): boolean {
  return event.type === "acquisition" && isMidPeriodDealEvent(event, interval);
}

export function isMidPeriodDisposal(
  event: BuilderLegalEntityDealEvent,
  interval?: LegalStructureAnalysisInterval,
): boolean {
  return event.type === "disposal" && isMidPeriodDealEvent(event, interval);
}

export function isMidPeriodDealEvent(
  event: BuilderLegalEntityDealEvent,
  interval?: LegalStructureAnalysisInterval,
): boolean {
  if (event.effective_month === null || !interval) {
    return false;
  }
  const fiscalYear = eventFiscalYear(
    event.effective_month,
    interval.financialYearEndMonth,
  );
  return fiscalYear !== null
    && fiscalYear >= interval.startFiscalYear
    && fiscalYear <= interval.endFiscalYear;
}

function formatEffectiveMonth(effectiveMonth: string): string {
  const [year, month] = effectiveMonth.split("-").map(Number);
  return `${MONTH_LABELS[month - 1]}-${String(year).slice(-2)}`;
}

export function dealEventPresentation(
  event: BuilderLegalEntityDealEvent,
  interval?: LegalStructureAnalysisInterval,
): LegalEntityDealEventPresentation {
  const label = event.type === "disposal"
    ? isMidPeriodDisposal(event, interval)
      ? "Mid-period disposal"
      : "Disposal"
    : isMidPeriodAcquisition(event, interval)
      ? "Mid-period acquisition"
      : "Acquisition";
  if (event.effective_month === null) {
    return { label, detail: "Effective month not identified" };
  }
  return {
    label,
    detail: event.type === "acquisition"
      ? `From ${formatEffectiveMonth(event.effective_month)}`
      : `Deconsolidated from ${formatEffectiveMonth(event.effective_month)}`,
  };
}

function isLegacyDealEventHighlight(
  highlight: string,
  type: BuilderLegalEntityDealEvent["type"],
): boolean {
  const normalized = highlight.trim().toLocaleLowerCase();
  if (type === "acquisition") {
    return [
      "acquisition",
      "acquisitions",
      "mid-period acquisition",
      "mid-period acquisitions",
      "mid-year acquisition",
      "mid-year acquisitions",
    ].includes(normalized);
  }
  return [
    "disposal",
    "disposals",
    "mid-period disposal",
    "mid-period disposals",
    "mid-year disposal",
    "mid-year disposals",
  ].includes(normalized);
}

export function visibleLegalEntityHighlights(
  highlights: string[],
  dealEvents: BuilderLegalEntityDealEvent[],
): string[] {
  return highlights.filter(
    (highlight) =>
      !dealEvents.some((event) =>
        isLegacyDealEventHighlight(highlight, event.type)),
  );
}

export function cloneLegalStructure(
  value: BuilderLegalStructure,
): BuilderLegalStructure {
  return structuredClone(value);
}

function writableStructure(value: BuilderLegalStructure) {
  return {
    presentation_currency: value.presentation_currency,
    presentation_currency_confirmed: value.presentation_currency_confirmed,
    groups: value.groups.map(({ id, name, position }) => ({
      id,
      name,
      position,
    })),
    entities: value.entities.map((entity) => ({
      id: entity.id,
      name: entity.name,
      activity_summary: entity.activity_summary,
      operating_currency: entity.operating_currency,
      deal_events: entity.deal_events,
      transaction_perimeter: entity.transaction_perimeter,
      display_group_id: entity.display_group_id,
      position: entity.position,
      parent_entity_id: entity.parent_entity_id,
      relationship_type: entity.relationship_type,
      active: entity.active,
    })),
  };
}

export function legalStructuresHaveSameWritableState(
  left: BuilderLegalStructure,
  right: BuilderLegalStructure,
): boolean {
  return JSON.stringify(writableStructure(left))
    === JSON.stringify(writableStructure(right));
}

export function normalizeLegalStructure(
  value: BuilderLegalStructure,
): BuilderLegalStructure {
  const groups = [...value.groups]
    .sort((left, right) => left.position - right.position)
    .map((group, position) => ({ ...group, position }));
  const activeByGroup = new Map<string, BuilderLegalStructureEntity[]>();
  const removed: BuilderLegalStructureEntity[] = [];
  for (const entity of value.entities) {
    if (!entity.active) {
      removed.push(entity);
      continue;
    }
    const key = entity.display_group_id ?? "";
    const entities = activeByGroup.get(key) ?? [];
    entities.push(entity);
    activeByGroup.set(key, entities);
  }
  const active = [...activeByGroup.values()].flatMap((entities) =>
    [...entities]
      .sort((left, right) => left.position - right.position)
      .map((entity, position) => ({ ...entity, position }))
  );
  return { ...value, groups, entities: [...active, ...removed] };
}

export function updateLegalEntity(
  value: BuilderLegalStructure,
  entityId: string,
  patch: Partial<BuilderLegalStructureEntity>,
): BuilderLegalStructure {
  return normalizeLegalStructure({
    ...value,
    entities: value.entities.map((entity) =>
      entity.id === entityId ? { ...entity, ...patch } : entity
    ),
  });
}

export function removeLegalEntity(
  value: BuilderLegalStructure,
  entityId: string,
): BuilderLegalStructure {
  return normalizeLegalStructure({
    ...value,
    entities: value.entities.map((entity) => {
      if (entity.id === entityId) {
        return {
          ...entity,
          active: false,
          parent_entity_id: null,
          relationship_type: null,
        };
      }
      if (entity.parent_entity_id === entityId) {
        return {
          ...entity,
          parent_entity_id: null,
          relationship_type: null,
        };
      }
      return entity;
    }),
  });
}

export function moveLegalEntity(
  value: BuilderLegalStructure,
  entityId: string,
  displayGroupId: string | null,
  beforeEntityId?: string,
): BuilderLegalStructure {
  const moved = value.entities.find((entity) => entity.id === entityId);
  if (!moved) return value;
  const remaining = value.entities.filter((entity) => entity.id !== entityId);
  const peers = remaining.filter(
    (entity) =>
      entity.active && entity.display_group_id === displayGroupId,
  );
  const targetPosition = beforeEntityId
    ? peers.findIndex((entity) => entity.id === beforeEntityId)
    : peers.length;
  return normalizeLegalStructure({
    ...value,
    entities: [
      ...remaining,
      {
        ...moved,
        display_group_id: displayGroupId,
        position: targetPosition < 0 ? peers.length : targetPosition,
      },
    ],
  });
}

export function addLegalEntity(
  value: BuilderLegalStructure,
  name: string,
  options: {
    operatingCurrency: string;
    activitySummary?: string | null;
    displayGroupId?: string | null;
    dealEvents?: BuilderLegalStructureEntity["deal_events"];
  } = {},
): BuilderLegalStructure {
  const displayGroupId = options.displayGroupId ?? null;
  return normalizeLegalStructure({
    ...value,
    entities: [
      ...value.entities,
      {
        id: crypto.randomUUID(),
        name: name.trim(),
        activity_summary: options.activitySummary?.trim() || null,
        operating_currency: options.operatingCurrency.trim().toUpperCase(),
        deal_events: options.dealEvents ?? [],
        transaction_perimeter: "inside",
        display_group_id: displayGroupId,
        position: value.entities.filter(
          (entity) =>
            entity.active && entity.display_group_id === displayGroupId,
        ).length,
        parent_entity_id: null,
        relationship_type: null,
        active: true,
        origin: "user",
        source_names: [],
      },
    ],
  });
}

export function updateLegalGroup(
  value: BuilderLegalStructure,
  groupId: string,
  patch: Partial<BuilderLegalStructureGroup>,
): BuilderLegalStructure {
  return normalizeLegalStructure({
    ...value,
    groups: value.groups.map((group) =>
      group.id === groupId ? { ...group, ...patch } : group
    ),
  });
}

export function validateLegalStructure(
  value: BuilderLegalStructure,
): LegalStructureValidationError[] {
  const errors: LegalStructureValidationError[] = [];
  if (!CURRENCY_CODE_PATTERN.test(value.presentation_currency)) {
    errors.push({
      path: "presentation_currency",
      message: "Presentation currency must be a three-letter code.",
    });
  }
  const groupNames = new Map<string, string>();
  const groupIds = new Set<string>();
  for (const group of value.groups) {
    groupIds.add(group.id);
    const name = group.name.trim();
    if (!name) {
      errors.push({
        path: `groups.${group.id}.name`,
        message: "Group name is required.",
      });
    }
    const key = name.toLocaleLowerCase();
    if (groupNames.has(key)) {
      errors.push({
        path: `groups.${group.id}.name`,
        message: "Group names must be unique.",
      });
    }
    groupNames.set(key, group.id);
  }
  const active = value.entities.filter((entity) => entity.active);
  const names = new Map<string, string>();
  for (const entity of active) {
    const name = entity.name.trim();
    if (!name) {
      errors.push({ path: `entities.${entity.id}.name`, message: "Name is required." });
    }
    const key = name.toLocaleLowerCase();
    if (names.has(key)) {
      errors.push({
        path: `entities.${entity.id}.name`,
        message: "Active entity names must be unique.",
      });
    }
    names.set(key, entity.id);
    if (!CURRENCY_CODE_PATTERN.test(entity.operating_currency)) {
      errors.push({
        path: `entities.${entity.id}.operating_currency`,
        message: "Operating currency must be a three-letter code.",
      });
    }
    if (
      entity.display_group_id
      && !groupIds.has(entity.display_group_id)
    ) {
      errors.push({
        path: `entities.${entity.id}.display_group_id`,
        message: "The selected group does not exist.",
      });
    }
    if (
      Boolean(entity.parent_entity_id)
      !== Boolean(entity.relationship_type)
    ) {
      errors.push({
        path: `entities.${entity.id}.relationship_type`,
        message: "Parent and relationship must be set together.",
      });
    }
    const eventTypes = entity.deal_events.map((event) => event.type);
    if (eventTypes.length !== new Set(eventTypes).size) {
      errors.push({
        path: `entities.${entity.id}.deal_events`,
        message: "Each deal event type can be used only once.",
      });
    }
    for (const event of entity.deal_events) {
      if (
        event.effective_month !== null
        && !DEAL_EVENT_MONTH_PATTERN.test(event.effective_month)
      ) {
        errors.push({
          path: `entities.${entity.id}.deal_events.${event.type}`,
          message: "Effective month must use YYYY-MM.",
        });
      }
    }
    const acquisitionMonth = entity.deal_events.find(
      (event) => event.type === "acquisition",
    )?.effective_month;
    const disposalMonth = entity.deal_events.find(
      (event) => event.type === "disposal",
    )?.effective_month;
    if (
      acquisitionMonth
      && disposalMonth
      && acquisitionMonth > disposalMonth
    ) {
      errors.push({
        path: `entities.${entity.id}.deal_events`,
        message: "Acquisition cannot be later than disposal.",
      });
    }
  }
  const activeIds = new Set(active.map((entity) => entity.id));
  for (const entity of active) {
    if (
      entity.parent_entity_id
      && !activeIds.has(entity.parent_entity_id)
    ) {
      errors.push({
        path: `entities.${entity.id}.parent_entity_id`,
        message: "The selected parent is not active.",
      });
    }
    const visited = new Set([entity.id]);
    let parentId = entity.parent_entity_id;
    while (parentId) {
      if (visited.has(parentId)) {
        errors.push({
          path: `entities.${entity.id}.parent_entity_id`,
          message: "Entity hierarchy cannot contain a cycle.",
        });
        break;
      }
      visited.add(parentId);
      parentId = active.find((candidate) => candidate.id === parentId)
        ?.parent_entity_id ?? null;
    }
  }
  return errors;
}
